// Portado de apps/admin/src/logic/documents.ts (PP-22) para que el portal del
// proveedor traduzca la elegibilidad igual que la consola. Hoy hay tres copias
// de esta traduccion (admin, tower/src/logic/eligibility.ts y esta): quedan
// reportadas para consolidarse en un paquete compartido en otra iteracion.
//
// Unica diferencia con la consola: los motivos de documento dicen "documento
// vencido: <tipo>" y "falta documento: <tipo>", la misma forma que usa la
// torre, porque el proveedor tiene que saber que papel renovar.
//
// Logica pura del sub-tab Flota: parseo de las respuestas de choferes y
// documentos con contracts, estado derivado de un documento con `hoy`
// inyectado (la API ya lo manda derivado; aca se recalcula para el detalle
// "vence en N dias" y para el semaforo por sujeto), agrupacion por sujeto y
// vocabulario visual (etiquetas, tono de badge, color de semaforo).
import {
  EXPIRY_NOTICE_DAYS,
  DriverListResponse,
  DocumentListResponse,
  ExpirySummaryResponse,
  type Driver,
  type CountByDocumentStatus,
  type CreateDocumentRequest,
  type Document,
  type DocumentStatus,
  type DocumentSubject,
  type DocumentSubjectType,
  type Eligibility,
  type IneligibilityReason,
  type RequiredDocuments,
  type UpdateDocumentRequest,
} from "@ucorp/contracts";

/** Color del semaforo de un documento (en admin vive en budgets.ts). */
export type StatusLightColor = "verde" | "ambar" | "rojo";

export function parseDrivers(crudo: unknown): Driver[] {
  return DriverListResponse.parse(crudo).drivers;
}

export function parseDocuments(crudo: unknown): DocumentListResponse {
  return DocumentListResponse.parse(crudo);
}

export function parseSummary(crudo: unknown): ExpirySummaryResponse {
  return ExpirySummaryResponse.parse(crudo);
}

/** Dias calendario entre dos fechas YYYY-MM-DD (positivo si `hasta` es posterior). Sin Date local: sin corrimientos de zona. */
export function daysBetween(from: string, to: string): number {
  const [a1, m1, d1] = from.split("-").map(Number);
  const [a2, m2, d2] = to.split("-").map(Number);
  const t1 = Date.UTC(a1 ?? 0, (m1 ?? 1) - 1, d1 ?? 1);
  const t2 = Date.UTC(a2 ?? 0, (m2 ?? 1) - 1, d2 ?? 1);
  return Math.round((t2 - t1) / 86_400_000);
}

/**
 * Estado derivado a la fecha `hoy` (misma regla que la API): vencido si la
 * fecha ya paso, por_vencer si vence en `diasAviso` dias o menos (hoy
 * inclusive), vigente si falta mas, sin_vencimiento si no vence.
 */
export function documentStatus(
  expiresAt: string | null,
  today: string,
  noticeDays: number = EXPIRY_NOTICE_DAYS,
): DocumentStatus {
  if (expiresAt === null) return "no_expiry";
  const days = daysBetween(today, expiresAt);
  if (days < 0) return "expired";
  if (days <= noticeDays) return "expiring_soon";
  return "effective";
}

/** "vence en 12 dias", "vencio hace 3 dias", "vence hoy" o "no vence". */
export function expiryDetail(expiresAt: string | null, today: string): string {
  if (expiresAt === null) return "no vence";
  const days = daysBetween(today, expiresAt);
  if (days === 0) return "vence hoy";
  if (days < 0) return `venció hace ${String(-days)} ${-days === 1 ? "día" : "días"}`;
  return `vence en ${String(days)} ${days === 1 ? "día" : "días"}`;
}

export const DOCUMENT_STATUS_LABEL: Record<DocumentStatus, string> = {
  effective: "Vigente",
  expiring_soon: "Por vencer",
  expired: "Vencido",
  no_expiry: "Sin vencimiento",
};

export const DOCUMENT_STATUS_COLOR: Record<DocumentStatus, StatusLightColor> = {
  effective: "verde",
  expiring_soon: "ambar",
  expired: "rojo",
  no_expiry: "verde",
};

/** De peor a mejor: el semaforo de un sujeto muestra su peor documento. */
const GRAVEDAD: readonly DocumentStatus[] = ["expired", "expiring_soon", "effective", "no_expiry"];

/** El peor estado del conjunto; undefined si no hay documentos. */
export function worstStatus(statuses: readonly DocumentStatus[]): DocumentStatus | undefined {
  for (const status of GRAVEDAD) {
    if (statuses.includes(status)) return status;
  }
  return undefined;
}

/** Clave estable de un sujeto para agrupar y para elegir en un select. */
export function subjectKey(subject: DocumentSubject): string {
  return `${subject.type}:${subject.providerId}:${subject.id}`;
}

/** Inversa de claveDeSujeto; undefined si la clave no tiene el formato. */
export function subjectFromKey(clave: string): DocumentSubject | undefined {
  const [type, providerId, ...resto] = clave.split(":");
  const id = resto.join(":");
  if ((type !== "vehicle" && type !== "driver") || providerId === undefined || id === "") {
    return undefined;
  }
  return { type, providerId, id };
}

/** Documentos agrupados por sujeto, manteniendo el orden de entrada. */
export function groupBySubject(documents: readonly Document[]): Map<string, Document[]> {
  const groups = new Map<string, Document[]>();
  for (const doc of documents) {
    const clave = subjectKey(doc.subject);
    const group = groups.get(clave);
    if (group === undefined) groups.set(clave, [doc]);
    else group.push(doc);
  }
  return groups;
}

/** Documentos de un sujeto concreto. */
export function subjectDocuments(
  documents: readonly Document[],
  subject: DocumentSubject,
): Document[] {
  const clave = subjectKey(subject);
  return documents.filter((d) => subjectKey(d.subject) === clave);
}

/** Choferes vigentes de un proveedor (en la nomina y sin bloqueo de la planta), ordenados por nombre. */
export function activeDriversOf(drivers: readonly Driver[], providerId: string): Driver[] {
  return drivers
    .filter((c) => c.providerId === providerId && c.active && c.inRoster)
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
}

/** Motivo de no elegibilidad (ADR-014 §2) en palabras del portal. */
export function ineligibilityReasonLabel(reason: IneligibilityReason): string {
  switch (reason.code) {
    case "chain_inactive":
      return "fuera de servicio (proveedor, empresa o nómina inactivos)";
    case "blocked_by_site":
      return "bloqueado por la planta";
    case "missing_document":
      return `falta documento: ${reason.documentType}`;
    case "expired_document":
      return `documento vencido: ${reason.documentType}`;
    case "vehicle_too_old":
      return `modelo ${String(reason.modelYear)} supera ${String(reason.maxAgeYears)} años`;
    case "model_year_unknown":
      return "sin año del modelo";
  }
}

/** Resumen de una elegibilidad: etiqueta corta y el detalle de los motivos. */
export function eligibilitySummary(eligibility: Eligibility): { label: string; detail: string } {
  if (eligibility.eligible) return { label: "Elegible", detail: "" };
  return {
    label: "No elegible",
    detail: eligibility.reasons.map(ineligibilityReasonLabel).join("; "),
  };
}

/** Total de documentos de un conteo por estado. */
export function countTotal(count: CountByDocumentStatus): number {
  return count.effective + count.expiring_soon + count.expired + count.no_expiry;
}

/** Borrador del formulario de documento -> cuerpo del request; null si esta incompleto. */
export interface DocumentDraft {
  subject: string;
  type: string;
  expiresAt: string;
  reference: string;
}

export function documentRequest(
  borrador: DocumentDraft,
): { subject: DocumentSubject; type: string; expiresAt: string | null; reference?: string } | null {
  const subject = subjectFromKey(borrador.subject);
  const type = borrador.type.trim();
  if (subject === undefined || type === "") return null;
  const reference = borrador.reference.trim();
  return {
    subject,
    type,
    expiresAt: borrador.expiresAt === "" ? null : borrador.expiresAt,
    ...(reference === "" ? {} : { reference }),
  };
}

// --- Ficha de documentos de un sujeto (PP-23) --------------------------------

/** Etiquetas de los tipos conocidos del catalogo abierto; el resto se muestra legible. */
const DOCUMENT_TYPE_LABEL: Readonly<Record<string, string>> = {
  habilitacion_municipal: "Habilitación municipal",
  seguro: "Seguro",
  vtv: "VTV",
  licencia: "Licencia de conducir",
  psicofisico: "Psicofísico",
};

/** Misma regla que la torre: guiones bajos a espacios y mayuscula inicial. */
export function documentTypeLabel(type: string): string {
  const known = DOCUMENT_TYPE_LABEL[type];
  if (known !== undefined) return known;
  const spaced = type.replace(/_/g, " ").trim();
  return spaced.length === 0 ? type : spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Tipos que la planta exige a este sujeto. Salen de `requiredDocuments` de la
 * planta (contratos 0.6.40), nunca de una lista fija del front. Con una API
 * anterior, lo unico que se sabe es lo que la elegibilidad reporta como
 * faltante o vencido: un tipo que la planta exige y esta en regla no aparece,
 * pero nada que la planta no exija aparece como faltante.
 */
export function requiredTypesFor(
  required: RequiredDocuments | undefined,
  subjectType: DocumentSubjectType,
  eligibility: Eligibility,
): string[] {
  if (required !== undefined) return [...new Set(required[subjectType])];
  const types: string[] = [];
  for (const reason of eligibility.reasons) {
    if ((reason.code === "missing_document" || reason.code === "expired_document") && !types.includes(reason.documentType)) {
      types.push(reason.documentType);
    }
  }
  return types;
}

/**
 * El documento de un tipo que vale para el sujeto: el que vence mas tarde, y
 * uno sin vencimiento antes que cualquiera. Si hay duplicados de antes de
 * PP-23, la renovacion actua sobre este.
 */
export function currentDocument(documents: readonly Document[], subject: DocumentSubject, type: string): Document | undefined {
  let best: Document | undefined;
  for (const doc of subjectDocuments(documents, subject)) {
    if (doc.type !== type) continue;
    if (best === undefined || laterExpiry(doc.expiresAt, best.expiresAt)) best = doc;
  }
  return best;
}

function laterExpiry(a: string | null, b: string | null): boolean {
  if (b === null) return false;
  if (a === null) return true;
  return a > b;
}

/** Una fila de la ficha: un tipo exigido (con o sin documento) o un documento que la planta no exige. */
export interface DocumentRow {
  type: string;
  required: boolean;
  /** undefined: falta (solo en un tipo exigido). */
  document: Document | undefined;
  /** "missing" cuando la planta lo exige y el sujeto no lo tiene. */
  status: DocumentStatus | "missing";
}

/**
 * Filas de la ficha de un sujeto: primero los tipos exigidos, en el orden de
 * la planta, despues los demas documentos que tenga. Un tipo que la planta no
 * exige nunca aparece como faltante.
 */
export function documentRows(
  requiredTypes: readonly string[],
  documents: readonly Document[],
  subject: DocumentSubject,
): DocumentRow[] {
  const rows: DocumentRow[] = requiredTypes.map((type) => {
    const document = currentDocument(documents, subject, type);
    return { type, required: true, document, status: document === undefined ? "missing" : document.status };
  });
  const others = [...new Set(subjectDocuments(documents, subject).map((d) => d.type))]
    .filter((type) => !requiredTypes.includes(type))
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  for (const type of others) {
    const document = currentDocument(documents, subject, type);
    if (document !== undefined) rows.push({ type, required: false, document, status: document.status });
  }
  return rows;
}

/**
 * Cargar o renovar (ADR-014 §2: un documento se carga una sola vez). Si el
 * sujeto ya tiene un documento de ese tipo, el segundo intento es una
 * renovacion que lo reemplaza; si no, un alta.
 */
export type UploadPlan = { kind: "create" } | { kind: "renew"; documentId: string };

export function uploadPlan(documents: readonly Document[], subject: DocumentSubject, type: string): UploadPlan {
  const current = currentDocument(documents, subject, type.trim());
  return current === undefined ? { kind: "create" } : { kind: "renew", documentId: current.id };
}

/** Lo que tiene el formulario de carga o renovacion, todo como texto. */
export interface UploadDraft {
  type: string;
  /** YYYY-MM-DD del input de fecha; vacio: no vence. */
  expiresAt: string;
  reference: string;
}

export type UploadRequest =
  | { kind: "create"; body: CreateDocumentRequest }
  | { kind: "renew"; documentId: string; body: UpdateDocumentRequest };

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Del borrador al request. null si falta el tipo o la fecha no es una fecha.
 * En la renovacion la referencia vacia viaja como null: la del papel anterior
 * no describe al nuevo.
 */
export function uploadRequest(draft: UploadDraft, subject: DocumentSubject, plan: UploadPlan): UploadRequest | null {
  const type = draft.type.trim();
  if (type === "") return null;
  if (plan.kind === "renew") {
    const body = renewalBody(draft);
    return body === null ? null : { kind: "renew", documentId: plan.documentId, body };
  }
  const expiresAt = expiryOf(draft);
  if (expiresAt === undefined) return null;
  const reference = draft.reference.trim();
  return { kind: "create", body: { subject, type, expiresAt, ...(reference === "" ? {} : { reference }) } };
}

/** Vencimiento del borrador: null si no vence, undefined si no es una fecha. */
function expiryOf(draft: UploadDraft): string | null | undefined {
  const date = draft.expiresAt.trim();
  if (date === "") return null;
  return CALENDAR_DATE.test(date) ? date : undefined;
}

/**
 * Cuerpo de una renovacion: nuevo vencimiento y referencia; el tipo no
 * cambia. null si la fecha no es una fecha.
 */
export function renewalBody(draft: UploadDraft): UpdateDocumentRequest | null {
  const expiresAt = expiryOf(draft);
  if (expiresAt === undefined) return null;
  const reference = draft.reference.trim();
  return { expiresAt, reference: reference === "" ? null : reference };
}

/** YYYY-MM-DD a DD/MM/YYYY sin pasar por Date: la fecha ya es la operativa de la planta. */
export function formatCalendarDate(date: string): string {
  const [year, month, day] = date.split("-");
  return `${day ?? ""}/${month ?? ""}/${year ?? ""}`;
}
